import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ScheduleBookingForm from "../components/landing/ScheduleBookingForm";
import Footer from "../components/landing/Footer";
import LandingNavbar from "../components/landing/LandingNavbar";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import { CALL_PIPELINE_FAQS } from "../data/seo/faqs";
import { buildServiceSchema } from "../data/seo/structuredData";
import { comMarketing, KW3_PIPELINE } from "../data/seo/kw3OnPage";

const keyFeatures = [
    {
        title: "Vertical Pack Templates",
        description: "Start from Dental, Real Estate, Retail, Support, Healthcare, or Education packs — each tuned with the right tools and prompts.",
    },
    {
        title: "Branching & Guards",
        description: "Conditional edges — by intent, urgency, language, or any custom variable — keep the agent on the right path every call.",
    },
    {
        title: "Tool Calls Mid-Call",
        description: "Look up Shopify orders, calendar slots, knowledge-base articles, your CRM, or any custom HTTP tool live on the call.",
    },
    {
        title: "Live Human Transfer",
        description: "Warm or cold transfer the moment escalation is needed — full call context handed over to your support team.",
    },
    {
        title: "Versioning & Rollback",
        description: "Publish a new pipeline version with one click. Roll back instantly. A/B route traffic between two versions safely.",
    },
    {
        title: "Multilingual",
        description: "16+ languages including English (US/UK/IN/AU/CA), Hindi, Tamil, Telugu, Kannada, Malayalam, Marathi, Bengali, Gujarati, Punjabi.",
    },
    {
        title: "Sub-200ms Latency",
        description: "Streamed voice with smart turn detection so callers feel like they're talking to a real, attentive agent — not a bot.",
    },
    {
        title: "Realtime Preview & Tests",
        description: "Step through every node with a sample call inside the editor. Inspect variables and timings without dialing.",
    },
    {
        title: "Same Pipeline, Inbound + Outbound",
        description: "Design once. The same call pipeline answers inbound calls and powers outbound dialing campaigns.",
    },
];

const pipelineNodes = [
    { name: "Greet", color: "from-emerald-500 to-teal-500" },
    { name: "Listen", color: "from-indigo-500 to-purple-500" },
    { name: "KB Lookup", color: "from-orange-500 to-pink-500" },
    { name: "Tool Call", color: "from-blue-500 to-cyan-500" },
    { name: "Book / Send SMS", color: "from-yellow-500 to-orange-500" },
    { name: "Transfer / End", color: "from-slate-500 to-slate-700" },
];

const verticalPacks = [
    {
        title: "Dental & Healthcare",
        description: "Greet, verify, check calendar, book or reschedule, send SMS reminder, transfer for clinical questions.",
    },
    {
        title: "Real Estate",
        description: "Qualify intent, capture budget + area, book viewings, drip-call cold leads with consent rules built in.",
    },
    {
        title: "Retail & D2C",
        description: "Order tracking, refund eligibility, abandoned-cart recovery, post-purchase NPS, and Shopify-aware tool calls.",
    },
    {
        title: "Local Services",
        description: "Quote requests, dispatch updates, after-hours overflow — never miss a call again, even at 2 AM.",
    },
];

const primaryKeywords = [
    "AI call pipeline",
    "Call flow builder",
    "Voice agent flow designer",
    "AI phone agent builder",
    "IVR replacement AI",
    "Conversational call flow",
    "Voice automation pipeline",
    "AI receptionist builder",
];

export default function CallPipelineMarketingPage() {
    const kw3 = comMarketing();
    usePageSeo({
        title: "AI Call Pipeline — Visual Voice Agent Flow Designer | FloGPT",
        description:
            "Design exactly what your voice agent says, looks up, and when it transfers — visually with the FloGPT Call Pipeline editor. Vertical packs, branching, tool calls, and versioning.",
        keywords: primaryKeywords.join(", "),
        canonical: "/ai-call-pipeline",
    });

    const serviceSchema = useMemo(
        () =>
            buildServiceSchema({
                name: "FloGPT AI Call Pipeline",
                description:
                    "Visual editor for designing AI voice agent call flows — greeting, intent detection, tool calls (calendars, Shopify, knowledge base, SMS, transfer), conditional branching, and end actions.",
                path: "/ai-call-pipeline",
                serviceType: "AI Voice Agent / Call Flow Designer",
                features: [
                    "Drag-to-connect call flow editor",
                    "Built-in tools: calendar, Shopify, KB, SMS, transfer, language switch",
                    "Custom HTTP tool registration",
                    "Vertical packs (Auto, Dental, Real Estate, Restaurants, etc.)",
                    "Branching by intent, time, or caller attribute",
                    "Versioning and rollback",
                    "Per-number overrides on shared templates",
                ],
            }),
        [],
    );

    return (
        <div className="bg-slate-950 text-slate-100 min-h-screen overflow-x-hidden">
            <LandingNavbar />

            <main>
                {/* HERO */}
                <section className="relative overflow-hidden border-b border-slate-900 pt-24 sm:pt-28" id="overview">
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-indigo-500/20 blur-3xl" />
                        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-purple-500/10 blur-3xl" />
                        <div className="absolute left-0 bottom-0 h-96 w-96 rounded-full bg-pink-500/10 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300 mb-6">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" />
                                    Call Pipeline · Visual · Versioned
                                </div>
                                <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-[3.35rem] lg:leading-[1.05]">
                                    Design Your Call Flow Visually.{" "}
                                    <motion.span
                                        className="inline-block"
                                        animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                        style={{
                                            background: "linear-gradient(90deg, #6366f1, #a855f7, #ec4899, #6366f1)",
                                            backgroundSize: "200% 100%",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                        }}
                                    >
                                        Publish in One Click.
                                    </motion.span>
                                </h1>
                                <p className="mt-6 max-w-xl text-lg text-slate-300 leading-relaxed">
                                    Drag nodes to design exactly how the agent greets, listens, looks things up, takes actions, and ends or transfers. Start from a vertical pack — Dental, Real Estate, Retail, Support — or build from scratch.
                                </p>
                                <div className="mt-8 flex flex-wrap items-center gap-4">
                                    <a
                                        href="#book-free-demo"
                                        className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-0.5"
                                    >
                                        Book a Free Demo
                                    </a>
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Start Building Free
                                    </Link>
                                </div>
                                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400">
                                    <span>· Vertical pack templates</span>
                                    <span>· Versioning + rollback</span>
                                    <span>· Inbound + outbound</span>
                                </div>
                            </motion.div>

                            {/* Pipeline editor preview */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="relative"
                            >
                                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-indigo-500/25 to-purple-500/25 blur-2xl" />
                                <div className="relative overflow-hidden rounded-3xl border border-slate-700/80 bg-slate-900/80 shadow-2xl p-6 sm:p-8">
                                    <div className="grid grid-cols-3 gap-4">
                                        {pipelineNodes.map((n, idx) => (
                                            <motion.div
                                                key={n.name}
                                                initial={{ opacity: 0, y: 10 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.05 * idx }}
                                                viewport={{ once: true }}
                                                className={`rounded-xl border border-slate-800 bg-gradient-to-r ${n.color} p-4 text-center text-sm font-semibold text-white shadow-lg`}
                                            >
                                                {n.name}
                                            </motion.div>
                                        ))}
                                    </div>
                                    <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-300 font-mono">
                                        {[
                                            "if (intent == 'book') -> node:CheckCalendar",
                                            "elif (intent == 'cancel') -> node:LookupAppointment",
                                            "elif (urgency > 0.7) -> node:TransferToHuman",
                                            "else -> node:AnswerFromKB",
                                        ].map((line, i) => (
                                            <div key={i} className="flex items-start gap-3">
                                                <span className="select-none text-slate-600 w-6 text-right">{i + 1}</span>
                                                <span className="whitespace-pre-wrap">{line}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-4 flex items-center justify-between border-t border-slate-800 pt-4 text-xs text-slate-400">
                                        <span>Pipeline: Dental Booking v3</span>
                                        <span>Published 12 min ago</span>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {kw3 ? (
                    <section className="mx-auto max-w-5xl px-6 lg:px-8 py-14">
                        <h2 className="text-2xl sm:text-3xl font-bold text-white">{KW3_PIPELINE.h2}</h2>
                        <p className="mt-4 max-w-3xl text-lg leading-relaxed text-slate-300">{KW3_PIPELINE.body}</p>
                        <img
                            src="/docs/screenshots/ai-calls-pipelines.png"
                            alt={KW3_PIPELINE.editorAlt}
                            width={KW3_PIPELINE.editorWidth}
                            height={KW3_PIPELINE.editorHeight}
                            className="mt-6 w-full rounded-2xl border border-slate-800"
                            loading="lazy"
                            decoding="async"
                        />
                    </section>
                ) : null}

                {/* WHY DIFFERENT */}
                <section className="mx-auto max-w-4xl px-6 lg:px-8 py-14 lg:py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="space-y-6"
                    >
                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-indigo-300 via-pink-300 to-orange-300 bg-clip-text text-transparent">
                                    The IVR is dead. The script is dead.
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Press-1 menus and rigid scripts make callers hang up. Hand-tuned prompts work for one flow and break the moment requirements change.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">
                                The Call Pipeline gives ops teams a visual canvas to evolve voice flows safely — while engineering keeps full control of the tools and guards each node calls.
                            </p>
                        </article>

                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-indigo-300 via-pink-300 to-orange-300 bg-clip-text text-transparent">
                                    Why this isn't just a flowchart
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Visual designers are easy. Voice-grade reliability isn't.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">FloGPT's Call Pipeline:</p>
                            <ul className="mt-4 space-y-3 text-slate-200">
                                {[
                                    "Streams audio with smart turn detection — sub-200ms perceived latency",
                                    "Treats KB lookups, calendar, Shopify and your tools as first-class nodes",
                                    "Versions every publish, with one-click rollback and A/B traffic split",
                                    "Reuses the same flow for inbound calls and outbound dialing campaigns",
                                ].map((line) => (
                                    <li key={line} className="flex items-start gap-3">
                                        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] bg-gradient-to-br from-indigo-400 via-pink-400 to-orange-400 shadow-[0_0_0_3px_rgba(99,102,241,0.15)]" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-4 text-slate-100 font-medium">It's the call-flow runtime your team would have built — without the year of engineering.</p>
                        </article>
                    </motion.div>
                </section>

                {/* VERTICAL PACKS */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Start with a vertical pack</h2>
                        <p className="mt-4 text-slate-300">
                            Production-ready templates tuned for your industry — clone, brand, ship.
                        </p>
                    </div>
                    <div className="mt-10 grid gap-6 md:grid-cols-2">
                        {verticalPacks.map((p, idx) => (
                            <motion.article
                                key={p.title}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.05 }}
                                viewport={{ once: true, amount: 0.2 }}
                                className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20 hover:border-indigo-400/30 transition-all"
                            >
                                <h3 className="text-xl font-semibold text-white">{p.title}</h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">{p.description}</p>
                            </motion.article>
                        ))}
                    </div>
                </section>

                {/* KEY FEATURES */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Everything the Call Pipeline can do</h2>
                        <p className="mt-4 text-slate-300">
                            Visual, versioned, voice-grade — with the tool calls your real flows need.
                        </p>
                    </div>

                    <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {keyFeatures.map((feature, index) => (
                            <article
                                key={feature.title}
                                className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 p-6 shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-1 hover:border-indigo-400/40"
                            >
                                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-500" />
                                <div className="flex items-center justify-between">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/15 border border-indigo-400/30">
                                        <svg className="h-5 w-5 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span className="rounded-full border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                </div>

                                <h3 className="mt-5 text-lg font-semibold text-white">{feature.title}</h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">{feature.description}</p>
                            </article>
                        ))}
                    </div>
                </section>

                {/* RELATED LINKS */}
                <section className="mx-auto max-w-5xl px-6 lg:px-8 py-12">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-7">
                        <h2 className="text-lg font-semibold text-white">Pair the Call Pipeline with</h2>
                        <p className="mt-2 text-slate-400 text-sm">A pipeline is just the canvas — it shines when you wire it into the rest of the platform.</p>
                        <div className="mt-5 grid gap-4 md:grid-cols-3">
                            <Link to="/ai-voice-agents" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-indigo-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Voice Agents Overview →</div>
                                <p className="mt-1 text-sm text-slate-400">See the full voice-agent product — phone numbers, languages, routing.</p>
                            </Link>
                            <Link to="/ai-outbound-triggers" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-indigo-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Outbound Triggers →</div>
                                <p className="mt-1 text-sm text-slate-400">Fire the same pipeline from webhooks, Shopify events, time-relative cadences.</p>
                            </Link>
                            <Link to="/ai-knowledge-base" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-indigo-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Knowledge Base →</div>
                                <p className="mt-1 text-sm text-slate-400">Ground every node's answers in your real docs and FAQs.</p>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* CTA + BOOKING */}
                <section id="book-free-demo" className="mx-auto max-w-6xl px-6 lg:px-8 pb-20">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white">See your own call pipeline in action</h2>
                        <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                            Book a free 20-minute consultation. We'll wire up a live demo on a real number you can dial.
                        </p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="mt-12 max-w-4xl mx-auto"
                    >
                        <ScheduleBookingForm
                            title="Book a Free Call Pipeline Demo"
                            subtitle="Tell us your use case — we'll have a working agent ready for the call"
                            submitLabel="Book Free Demo"
                        />
                    </motion.div>
                </section>

                <FAQSection
                    id="call-pipeline"
                    eyebrow="Call Pipeline · FAQ"
                    heading="Common questions about Call Pipelines"
                    items={CALL_PIPELINE_FAQS}
                />
            </main>
            <Footer />

            <JsonLd id="service-call-pipeline" data={serviceSchema} />
        </div>
    );
}
